import React, { useRef, useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import * as S from "./ChatInterfaceStyles";
import { FaMicrophone } from "react-icons/fa";
import axios from "axios";
import { apiUrl, API_BASE } from "../utils/api";
import { Attachment } from "./ChatInterface";

const AUDIO_EXTENSIONS = [".mp3", ".m4a", ".wav", ".aac", ".ogg"];
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp"];
const ALLOWED_EXTENSIONS = [
  ...IMAGE_EXTENSIONS,
  "pdf", "doc", "docx", "txt", "csv", "json",
  "mp3", "m4a", "wav", "aac", "ogg", "mp4", "mov", "webm",
];
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const MicrophoneIcon = FaMicrophone as unknown as React.FC<any>;

const AutoGrowingTextarea = styled.textarea`
  flex-grow: 1;
  border: none;
  outline: none;
  padding: 8px 6px;
  font-size: 0.95rem;
  background-color: transparent;
  color: #0f172a;
  resize: none;
  max-height: 150px;
  overflow-y: auto;
  line-height: 1.5;
  min-height: 32px;

  &::placeholder {
    color: #94a3b8;
  }
`;

interface StagedFile {
  file?: File;
  id: string;
  preview?: string;
  progress: number;
  uploaded?: Attachment;
  error?: string;
  type?: "file" | "folder" | "audio";
  name?: string;
  size?: number;
}

interface ChatInputProps {
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  sendMessage: (messageContent: string, filePath?: string, attachments?: Attachment[], folder_info?: { name: string; file_count: number }) => void;
  handleFileUpload: (files: FileList) => Promise<string>;
  handleAudioUpload: (file: File) => Promise<string>;
  isEmpty?: boolean;
  isIngestionContext?: boolean;
}

const isImageFile = (name: string) => {
  const ext = name.split(".").pop()?.toLowerCase() || "";
  return IMAGE_EXTENSIONS.includes(ext);
};

const getFileIcon = (name: string): string => {
  const ext = name.split(".").pop()?.toLowerCase() || "";
  if (IMAGE_EXTENSIONS.includes(ext)) return "🖼️";
  if (ext === "pdf") return "📄";
  if (ext === "doc" || ext === "docx") return "📝";
  if (ext === "txt") return "📃";
  if (ext === "csv") return "📊";
  if (ext === "json") return "🔧";
  if (["mp3", "m4a", "wav", "aac", "ogg"].includes(ext)) return "🎵";
  if (["mp4", "mov", "webm"].includes(ext)) return "🎬";
  return "📎";
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ChatInput: React.FC<ChatInputProps> = ({ input, setInput, sendMessage, handleFileUpload, handleAudioUpload, isEmpty, isIngestionContext }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [dragError, setDragError] = useState<string | null>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const dragCounter = useRef(0);

  // Legacy state for backward compat with existing resume upload
  const [serverFilePath, setServerFilePath] = useState<string | null>(null);
  const [uploadCount, setUploadCount] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [selectionSource, setSelectionSource] = useState<"files" | "folder" | null>(null);
  const [folderName, setFolderName] = useState<string | null>(null);

  const resetFileInputs = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (folderInputRef.current) folderInputRef.current.value = "";
  };

  // Validate a single file
  const validateFile = (file: File): string | null => {
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `"${file.name}" — unsupported file type (.${ext})`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `"${file.name}" — file too large (${formatFileSize(file.size)}, max 20MB)`;
    }
    return null;
  };

  // Upload a single attachment file
  const uploadAttachment = async (staged: StagedFile): Promise<Attachment | null> => {
    const formData = new FormData();
    formData.append("file", staged.file!); // We only call this for files
    try {
      const res = await axios.post(apiUrl("/upload"), formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 50;
          setStagedFiles(prev =>
            prev.map(f => f.id === staged.id ? { ...f, progress: pct } : f)
          );
        },
      });
      const fileUrl = res.data.signed_url || `${API_BASE}/static/${res.data.filename}`;
      return {
        id: staged.id,
        file_name: staged.name || staged.file!.name,
        file_type: staged.file?.type || `application/${(staged.name || staged.file!.name).split('.').pop()}`,
        file_size: staged.size || staged.file!.size,
        file_url: fileUrl,
        gcs_uri: res.data.gcs_uri || undefined,
        local_path: res.data.local_path || undefined,
        preview_url: (staged.name && isImageFile(staged.name)) ? fileUrl : undefined,
      };
    } catch (err) {
      console.error("Attachment upload failed:", err);
      setStagedFiles(prev =>
        prev.map(f => f.id === staged.id ? { ...f, error: "Upload failed" } : f)
      );
      return null;
    }
  };

  // Add files to staging area
  const addFilesToStage = useCallback(async (files: File[]) => {
    const errors: string[] = [];
    const valid: StagedFile[] = [];

    for (const file of files) {
      const err = validateFile(file);
      if (err) {
        errors.push(err);
        continue;
      }
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const preview = isImageFile(file.name) ? URL.createObjectURL(file) : undefined;
      valid.push({ file, id, preview, progress: 0, type: "file", name: file.name, size: file.size });
    }

    if (errors.length > 0) {
      setDragError(errors.join("\n"));
      setTimeout(() => setDragError(null), 4000);
    }

    if (valid.length === 0) return;
    setStagedFiles(prev => [...prev, ...valid]);

    // Upload each file
    for (const staged of valid) {
      const uploaded = await uploadAttachment(staged);
      if (uploaded) {
        setStagedFiles(prev =>
          prev.map(f => f.id === staged.id ? { ...f, progress: 100, uploaded } : f)
        );
      }
    }
  }, []);

  // Remove a staged file
  const removeStagedFile = (id: string) => {
    setStagedFiles(prev => {
      const removed = prev.find(f => f.id === id);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);

      if (removed?.type === "folder" || removed?.type === "audio") {
        setServerFilePath(null);
        setUploadCount(0);
        setFolderName(null);
        setSelectionSource(null);
      }

      return prev.filter(f => f.id !== id);
    });
  };

  // Drag-and-drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      addFilesToStage(files);
    }
  };

  // File input change — determine if it's attachment or legacy resume flow
  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Use new attachment flow for all files
    addFilesToStage(Array.from(files));
    resetFileInputs();
  };

  // Folder upload — keep legacy behavior for resume batch
  const onFolderChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Extract folder name from the first file's webkitRelativePath
    let currentFolderName = "Uploaded Folder";
    if (files.length > 0 && (files[0] as any).webkitRelativePath) {
      const path = (files[0] as any).webkitRelativePath;
      currentFolderName = path.split('/')[0];
    }
    setFolderName(currentFolderName);
    setUploadCount(files.length);
    setSelectionSource("folder");

    // ADDED: Immediate UI feedback for folder selection
    const tempId = `folder-temp-${Date.now()}`;
    setStagedFiles(prev => [...prev.filter(f => f.type !== "folder"), {
      id: tempId,
      type: "folder",
      name: currentFolderName,
      progress: 50, // Show some progress immediately
      size: 0,
      uploaded: undefined // Not yet uploaded
    }]);

    try {
      const path = await handleFileUpload(files);
      setServerFilePath(path);

      // Add folder to staged files for preview
      const id = `folder-${Date.now()}`;
      setStagedFiles(prev => [
        ...prev.filter(f => f.type !== "folder" && f.id !== tempId),
        {
          id,
          type: "folder",
          name: currentFolderName,
          progress: 100,
          size: 0,
          uploaded: {} as Attachment // Mark as uploaded to satisfy anyUploading check
        }
      ]);
    } catch (error) {
      console.error("Folder upload failed:", error);
      setServerFilePath(null);
      // Remove placeholder on error
      setStagedFiles(prev => prev.filter(f => f.id !== tempId));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSendMessage = () => {
    let messageContent = input.trim();
    const attachments = stagedFiles
      .filter(f => f.uploaded && f.uploaded.file_name) // Filter out dummy attachments
      .map(f => f.uploaded!);

    // Legacy file path handling should be deferred until after we decide if it's attachment-only
    // or has message content. We removed the redundant block here.

    if (!messageContent && attachments.length === 0 && !serverFilePath) return;

    // Determine if this is attachment-only (no user text, just files)
    const isAttachmentOnly = !messageContent && attachments.length > 0 && !serverFilePath;

    if (isAttachmentOnly) {
      const isAudioAttachment = attachments.length === 1 &&
        AUDIO_EXTENSIONS.some(ext => attachments[0].file_name.toLowerCase().endsWith(ext));

      // Automatic description for attachment-only messages (ChatGPT-like)
      const agentPath = attachments[0].gcs_uri || attachments[0].local_path || attachments[0].file_url;
      const defaultPrompt = isAudioAttachment
        ? `Transcribe the uploaded audio file: ${attachments[0].file_name} at ${agentPath}`
        : isIngestionContext
          ? `Ingest the uploaded file at ${agentPath}`
          : attachments.length === 1
            ? `Describe and summarize the uploaded file: ${attachments[0].file_name}`
            : `Describe and summarize these ${attachments.length} uploaded files.`;

      const fileDetails = attachments.map(att => {
        const sizeMB = (att.file_size / (1024 * 1024)).toFixed(2);
        const agentUrl = att.gcs_uri || att.file_url;
        return `- ${att.file_name} (${att.file_type}, ${sizeMB} MB): ${agentUrl}`;
      }).join('\n');

      const finalContent = `${defaultPrompt}\n\nAttached files:\n${fileDetails}`;
      sendMessage(finalContent, undefined, attachments);
    } else if (attachments.length > 0 && messageContent) {
      // User typed text + attached files: append file URLs so agent can reference them
      const isAudioAttachment = attachments.length === 1 &&
        AUDIO_EXTENSIONS.some(ext => attachments[0].file_name.toLowerCase().endsWith(ext));

      if (isAudioAttachment && !messageContent.toLowerCase().includes("transcrib")) {
        const audioPath = attachments[0].gcs_uri || attachments[0].file_url;
        // If user didn't mention transcribing, but it's an audio file, remind the agent to transcribe it first
        messageContent = `Please transcribe this audio file at ${audioPath} and then address my query: ${messageContent}`;
      }

      const fileDetails = attachments.map(att => {
        const sizeMB = (att.file_size / (1024 * 1024)).toFixed(2);
        const agentUrl = att.gcs_uri || att.file_url;
        return `- ${att.file_name} (${att.file_type}, ${sizeMB} MB): ${agentUrl}`;
      }).join('\n');
      messageContent = `${messageContent}\n\nAttached files:\n${fileDetails}`;
      sendMessage(messageContent, serverFilePath || undefined, attachments);
    } else {
      // Text-only or legacy file path
      const isAudio =
        serverFilePath &&
        AUDIO_EXTENSIONS.some(ext => serverFilePath.toLowerCase().endsWith(ext));

      if (isAudio && !messageContent) {
        messageContent = `Transcribe the audio file: ${serverFilePath}`;
      } else if (isAudio && messageContent && !messageContent.toLowerCase().includes("transcrib")) {
        messageContent = `Please transcribe this audio file and then address my query: ${messageContent} ${serverFilePath}`;
      } else if (serverFilePath && !messageContent) {
        if (isIngestionContext) {
          if (selectionSource === "folder") {
            messageContent = `Ingest the folder "${folderName}" (${uploadCount} files) at ${serverFilePath}`;
          } else {
            messageContent = `Ingest the uploaded file at ${serverFilePath}`;
          }
        } else if (selectionSource === "folder") {
          messageContent = `Describe and summarize the folder "${folderName}" (${uploadCount} files) at ${serverFilePath}`;
        } else {
          messageContent = `Describe and summarize the uploaded file at ${serverFilePath}`;
        }
      } else if (serverFilePath && messageContent) {
        messageContent = `${messageContent} ${serverFilePath}`;
      }

      const folder_info = (selectionSource === "folder" && folderName)
        ? { name: folderName, file_count: uploadCount }
        : undefined;
      sendMessage(messageContent, serverFilePath || undefined, undefined, folder_info);
    }

    // Reset everything
    setInput("");
    stagedFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    setStagedFiles([]);
    setServerFilePath(null);
    setUploadCount(0);
    setFolderName(null);
    setSelectionSource(null);
    resetFileInputs();
  };

  const handleMicrophoneClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      mediaRecorder.current.ondataavailable = (event) => {
        audioChunks.current.push(event.data);
      };
      mediaRecorder.current.onstop = async () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/wav' });
        const uniqueName = `audio_${Date.now()}.wav`;
        const audioFile = new File([audioBlob], uniqueName, {
          type: "audio/wav",
        });
        try {
          const path = await handleAudioUpload(audioFile);
          setServerFilePath(path);

          // Add audio to staged files for preview
          const id = `audio-${Date.now()}`;
          setStagedFiles(prev => [...prev.filter(f => f.type !== "audio"), {
            id,
            type: "audio",
            name: uniqueName,
            progress: 100,
            size: audioFile.size,
            uploaded: {} as Attachment // Mark as uploaded to satisfy anyUploading check
          }]);
        } catch (error) {
          console.error("Audio upload failed:", error);
        }
        audioChunks.current = [];
      };
      mediaRecorder.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current) {
      mediaRecorder.current.stop();
      setIsRecording(false);
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [input]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      stagedFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    };
  }, []);

  const allUploaded = stagedFiles.length > 0 && stagedFiles.every(f => f.uploaded || f.error);
  const anyUploading = stagedFiles.some(f => !f.uploaded && !f.error);
  const hasContent = input.trim() || stagedFiles.length > 0 || serverFilePath;

  return (
    <S.InputContainer
      $isEmpty={isEmpty}
      style={{ paddingLeft: '0px', paddingRight: '0px' }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <S.InputWrapper>
        {/* Drag overlay */}
        {isDragging && (
          <S.DragOverlay>
            <S.DragOverlayText>📎 Drop files here to attach</S.DragOverlayText>
          </S.DragOverlay>
        )}

        {/* Error toast */}
        {dragError && (
          <div style={{
            padding: '8px 14px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            fontSize: '12px',
            color: '#dc2626',
            whiteSpace: 'pre-line',
          }}>
            {dragError}
          </div>
        )}

        {/* Attachment preview strip */}
        {stagedFiles.length > 0 && (
          <S.AttachmentPreviewStrip>
            {stagedFiles.map(staged => (
              <S.AttachmentPreviewTile key={staged.id} $isImage={!!staged.preview}>
                {staged.preview ? (
                  <>
                    <S.PreviewTileImage src={staged.preview} alt={staged.name} style={{ opacity: !staged.uploaded && !staged.error ? 0.6 : 1 }} />
                    {!staged.uploaded && !staged.error && (
                      <S.ImageCircularProgressContainer>
                        <S.CircularLoader />
                      </S.ImageCircularProgressContainer>
                    )}
                  </>
                ) : (
                  <S.PreviewTileFile>
                    <S.CircularProgressContainer>
                      {!staged.uploaded && !staged.error && staged.type === "file" && (
                        <S.CircularLoader />
                      )}
                      <S.PreviewFileIcon>
                        {staged.type === "folder" ? "📁" :
                          staged.type === "audio" ? "🎵" :
                            getFileIcon(staged.name || "")}
                      </S.PreviewFileIcon>
                    </S.CircularProgressContainer>
                    <S.PreviewFileInfo>
                      <S.PreviewFileName>{staged.name}</S.PreviewFileName>
                      <S.PreviewFileSize>
                        {staged.error ? (
                          <span style={{ color: '#ef4444' }}>{staged.error}</span>
                        ) : staged.type === "folder" ? (
                          `${uploadCount} files`
                        ) : (
                          formatFileSize(staged.size || 0)
                        )}
                      </S.PreviewFileSize>
                    </S.PreviewFileInfo>
                  </S.PreviewTileFile>
                )}
                <S.PreviewTileRemove onClick={() => removeStagedFile(staged.id)}>×</S.PreviewTileRemove>
              </S.AttachmentPreviewTile>
            ))}
          </S.AttachmentPreviewStrip>
        )}

        {/* Legacy upload status - Suppressed for cleaner UI as requested */}
        {/* {isUploading && (
          <S.FileName>
            Uploading {uploadCount} file{uploadCount === 1 ? "" : "s"}...
          </S.FileName>
        )}
        {!isUploading && serverFilePath && selectionSource === "folder" && (
          <S.FileName>
            Ready to ingest {uploadCount} file{uploadCount === 1 ? "" : "s"} from {serverFilePath}
          </S.FileName>
        )} */}

        <S.InputRow>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <S.FileInputLabel htmlFor="file-upload" title="Attach files">
              <S.AttachmentIcon>📎</S.AttachmentIcon>
              <S.HiddenFileInput
                id="file-upload"
                type="file"
                accept=".pdf,.doc,.docx,.txt,.csv,.json,.png,.jpg,.jpeg,.gif,.webp,.bmp,.mp3,.m4a,.wav,.aac,.ogg,.mp4,.mov,.webm"
                multiple
                onChange={onFileChange}
                ref={fileInputRef}
              />
            </S.FileInputLabel>
            <S.FileInputLabel htmlFor="folder-upload" title="Upload entire folder">
              <S.AttachmentIcon>📁</S.AttachmentIcon>
              <S.HiddenFileInput
                id="folder-upload"
                type="file"
                accept=".pdf,.doc,.docx,.mp3,.m4a,.wav,.aac,.ogg,.mp4,.mov,.webm"
                multiple
                {...({ webkitdirectory: "true", directory: "true" } as React.HTMLProps<HTMLInputElement>)}
                onChange={onFolderChange}
                ref={folderInputRef}
              />
            </S.FileInputLabel>
            <div style={{ width: 1, height: 24, background: '#e2e8f0', margin: '0 4px' }} />
          </div>
          <AutoGrowingTextarea
            rows={1}
            placeholder={anyUploading ? "Uploading files..." : "Type your message..."}
            value={input}
            ref={textareaRef}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <S.IconButton
              onClick={handleMicrophoneClick}
              title={isRecording ? "Stop recording" : "Record audio"}
              style={{
                backgroundColor: isRecording ? 'rgba(249, 115, 22, 0.18)' : undefined,
                color: isRecording ? '#f97316' : '#94a3b8',
              }}
            >
              <MicrophoneIcon size={16} />
            </S.IconButton>
            <S.RectangleButton
              onClick={handleSendMessage}
              disabled={anyUploading || isUploading || !hasContent}
              title={anyUploading ? "Waiting for upload" : undefined}
            >
              <span style={{ marginRight: "6px", fontSize: "13px" }}>Send</span>
            </S.RectangleButton>
          </div>
        </S.InputRow>
      </S.InputWrapper>
    </S.InputContainer>
  );
};

export default ChatInput;
